import React from 'react';
import {
  AlertTriangle,
  X,
  ArrowRight,
  Info,
  Music,
  FileAudio,
  Image as ImageIcon,
  UploadCloud,
  CheckCircle2,
} from 'lucide-react';

/**
 * Categorize a list of validation error strings or structured errors
 * into the 5 mandatory groups:
 * 1. Release Information
 * 2. Track Credits
 * 3. Audio
 * 4. Cover Art
 * 5. Uploads
 */
export function groupValidationErrors(rawErrors = [], validationResult = null) {
  const groups = {
    releaseInfo: [],
    trackCredits: [],
    audio: [],
    coverArt: [],
    uploads: [],
  };

  const allMessages = [];

  if (Array.isArray(rawErrors)) {
    rawErrors.forEach((e) => {
      if (typeof e === 'string' && e.trim()) allMessages.push(e.trim());
      else if (e && e.message) allMessages.push(e.message.trim());
    });
  } else if (typeof rawErrors === 'object' && rawErrors !== null) {
    Object.entries(rawErrors).forEach(([key, val]) => {
      if (typeof val === 'string' && val.trim()) {
        allMessages.push({ fieldKey: key, message: val.trim() });
      }
    });
  }

  if (validationResult?.releaseErrors) {
    validationResult.releaseErrors.forEach((msg) => {
      if (!allMessages.some((m) => (typeof m === 'string' ? m : m.message) === msg)) {
        allMessages.push(msg);
      }
    });
  }

  if (validationResult?.missingFieldsByTrack) {
    Object.entries(validationResult.missingFieldsByTrack).forEach(([trackNum, fields]) => {
      fields.forEach((f) => {
        const msg = `Track ${trackNum}: ${f}`;
        if (!allMessages.some((m) => (typeof m === 'string' ? m : m.message) === msg)) {
          allMessages.push(msg);
        }
      });
    });
  }

  allMessages.forEach((item) => {
    const msg = typeof item === 'string' ? item : item.message;
    const key = typeof item === 'object' ? item.fieldKey : '';
    const lower = msg.toLowerCase();

    // 1. Audio errors
    if (
      lower.includes('audio') ||
      lower.includes('wav') ||
      lower.includes('flac') ||
      lower.includes('mp3') ||
      lower.includes('track count') ||
      lower.includes('single releases must contain') ||
      lower.includes('ep releases must contain') ||
      lower.includes('album releases must contain') ||
      lower.includes('master audio')
    ) {
      groups.audio.push({
        message: msg,
        fieldId: 'audio-upload-dropzone',
        stepNumber: 2,
        category: 'Audio',
      });
      return;
    }

    // 2. Cover Art errors
    if (
      lower.includes('cover') ||
      lower.includes('artwork') ||
      lower.includes('3000') ||
      lower.includes('square aspect ratio')
    ) {
      groups.coverArt.push({
        message: msg,
        fieldId: 'cover-art-dropzone',
        stepNumber: 4,
        category: 'Cover Art',
      });
      return;
    }

    // 3. Uploads (AI declaration PDF, Sample clearance links/proof)
    if (
      lower.includes('ai declaration') ||
      lower.includes('sample link') ||
      lower.includes('samples were used') ||
      lower.includes('ai generation is declared')
    ) {
      const trackMatch = msg.match(/Track\s*(\d+)/i);
      const trackNum = trackMatch ? parseInt(trackMatch[1], 10) : 1;
      const trackIdx = trackNum - 1;
      const fieldId = lower.includes('ai')
        ? `track-${trackIdx}-ai-doc`
        : `track-${trackIdx}-sample-0`;

      groups.uploads.push({
        message: msg,
        fieldId,
        stepNumber: 3,
        trackNumber: trackNum,
        category: 'Uploads',
      });
      return;
    }

    // 4. Track Credits errors
    const trackMatch = msg.match(/Track\s*(\d+)/i);
    if (
      trackMatch ||
      lower.includes('composer') ||
      lower.includes('lyricist') ||
      lower.includes('performer') ||
      lower.includes('track title') ||
      lower.includes('track language') ||
      key.startsWith('track_')
    ) {
      const trackNum = trackMatch ? parseInt(trackMatch[1], 10) : 1;
      const trackIdx = trackNum - 1;
      let fieldId = `track-${trackIdx}-title`;

      if (lower.includes('composer')) {
        fieldId = `track-${trackIdx}-comp-0`;
      } else if (lower.includes('lyricist')) {
        fieldId = `track-${trackIdx}-lyr-0`;
      } else if (lower.includes('language')) {
        fieldId = `track-${trackIdx}-language`;
      } else if (lower.includes('performer')) {
        fieldId = `track-${trackIdx}-perf-0`;
      }

      groups.trackCredits.push({
        message: msg,
        fieldId,
        stepNumber: 3,
        trackNumber: trackNum,
        category: 'Track Credits',
      });
      return;
    }

    // 5. Release Information (Release Title, Legal Name, Genre, Mood, Date, C/P Line, ISRC)
    let fieldId = 'rel-title';
    if (lower.includes('legal name') || key === 'legalName') fieldId = 'rel-legal-name';
    else if (lower.includes('genre') || key === 'genre') fieldId = 'dist-genre';
    else if (lower.includes('mood') || key === 'mood') fieldId = 'dist-mood';
    else if (lower.includes('c line') || lower.includes('copyright') || key === 'cLine') fieldId = 'c-line';
    else if (lower.includes('p line') || lower.includes('phonogram') || key === 'pLine') fieldId = 'p-line';
    else if (lower.includes('release date') || key === 'releaseDate') fieldId = 'dist-release-date';
    else if (lower.includes('isrc') || key === 'isrc') fieldId = 'dist-isrc';
    else if (lower.includes('primary artist') || key === 'primaryArtists') fieldId = 'primary-artist-0';
    else if (lower.includes('release type') || key === 'releaseType') fieldId = 'release-type-selector';

    groups.releaseInfo.push({
      message: msg,
      fieldId,
      stepNumber: fieldId === 'release-type-selector' ? 1 : 3,
      category: 'Release Information',
    });
  });

  return groups;
}

export default function ValidationErrorsModal({
  isOpen,
  onClose,
  errors = {},
  validationResult = null,
  onFocusField = () => {},
}) {
  if (!isOpen) return null;

  const grouped = groupValidationErrors(errors, validationResult);
  const totalCount =
    grouped.releaseInfo.length +
    grouped.trackCredits.length +
    grouped.audio.length +
    grouped.coverArt.length +
    grouped.uploads.length;

  const handleItemClick = (item) => {
    onFocusField(item);
    onClose();

    // Focus and scroll to field element
    setTimeout(() => {
      if (item.fieldId) {
        const el = document.getElementById(item.fieldId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          try {
            el.focus({ preventScroll: true });
          } catch {}
          // Highlight flash
          el.classList.add('dist-field-highlight');
          setTimeout(() => el.classList.remove('dist-field-highlight'), 2000);
        }
      }
    }, 200);
  };

  const sections = [
    {
      id: 'releaseInfo',
      title: 'Release Information',
      icon: <Info size={17} color="#d9fb06" />,
      items: grouped.releaseInfo,
      badgeColor: 'rgba(217, 251, 6, 0.15)',
      badgeBorder: 'rgba(217, 251, 6, 0.4)',
    },
    {
      id: 'trackCredits',
      title: 'Track Credits',
      icon: <Music size={17} color="#60a5fa" />,
      items: grouped.trackCredits,
      badgeColor: 'rgba(96, 165, 250, 0.15)',
      badgeBorder: 'rgba(96, 165, 250, 0.4)',
    },
    {
      id: 'audio',
      title: 'Audio Specifications',
      icon: <FileAudio size={17} color="#f59e0b" />,
      items: grouped.audio,
      badgeColor: 'rgba(245, 158, 11, 0.15)',
      badgeBorder: 'rgba(245, 158, 11, 0.4)',
    },
    {
      id: 'coverArt',
      title: 'Cover Artwork',
      icon: <ImageIcon size={17} color="#ec4899" />,
      items: grouped.coverArt,
      badgeColor: 'rgba(236, 72, 153, 0.15)',
      badgeBorder: 'rgba(236, 72, 153, 0.4)',
    },
    {
      id: 'uploads',
      title: 'Uploads & Declarations',
      icon: <UploadCloud size={17} color="#a78bfa" />,
      items: grouped.uploads,
      badgeColor: 'rgba(167, 139, 250, 0.15)',
      badgeBorder: 'rgba(167, 139, 250, 0.4)',
    },
  ];

  return (
    <div
      className="dist-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="validation-modal-title"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.78)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.25rem',
        animation: 'distModalFadeIn 0.2s ease-out',
      }}
    >
      <div
        className="dist-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#121214',
          border: '1px solid rgba(255, 107, 107, 0.35)',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '680px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.6), 0 0 30px rgba(255, 107, 107, 0.15)',
          overflow: 'hidden',
          animation: 'distModalScaleIn 0.25s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg, rgba(255, 80, 80, 0.08) 0%, transparent 100%)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                background: 'rgba(255, 107, 107, 0.15)',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ff6b6b',
              }}
            >
              <AlertTriangle size={20} />
            </div>
            <div>
              <h3
                id="validation-modal-title"
                style={{
                  margin: 0,
                  fontSize: '1.15rem',
                  fontWeight: 700,
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                Release Requirements
                <span
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    padding: '0.15rem 0.55rem',
                    borderRadius: '20px',
                    background: 'rgba(255, 107, 107, 0.2)',
                    color: '#ff8585',
                    border: '1px solid rgba(255, 107, 107, 0.35)',
                  }}
                >
                  {totalCount} {totalCount === 1 ? 'Issue' : 'Issues'}
                </span>
              </h3>
              <p
                style={{
                  margin: '0.2rem 0 0 0',
                  fontSize: '0.82rem',
                  color: 'rgba(255, 255, 255, 0.6)',
                }}
              >
                Click any issue to focus directly on that field.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#cccccc',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
              e.currentTarget.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
              e.currentTarget.style.color = '#cccccc';
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Body with Grouped Sections */}
        <div
          style={{
            padding: '1.25rem 1.5rem',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem',
          }}
        >
          {sections.map((sec) => {
            if (sec.items.length === 0) return null;

            return (
              <div
                key={sec.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.025)',
                  border: '1px solid rgba(255, 255, 255, 0.07)',
                  borderRadius: '12px',
                  padding: '1rem',
                }}
              >
                {/* Section Header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.75rem',
                    paddingBottom: '0.5rem',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {sec.icon}
                    <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#f3f4f6' }}>
                      {sec.title}
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      padding: '0.15rem 0.5rem',
                      borderRadius: '10px',
                      background: sec.badgeColor,
                      border: `1px solid ${sec.badgeBorder}`,
                      color: '#ffffff',
                    }}
                  >
                    {sec.items.length}
                  </span>
                </div>

                {/* Section Error Items */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {sec.items.map((item, idx) => {
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleItemClick(item)}
                        style={{
                          background: 'rgba(255, 107, 107, 0.06)',
                          border: '1px solid rgba(255, 107, 107, 0.2)',
                          borderRadius: '8px',
                          padding: '0.65rem 0.85rem',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.75rem',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          color: '#ffb3b3',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 107, 107, 0.14)';
                          e.currentTarget.style.borderColor = 'rgba(255, 107, 107, 0.45)';
                          e.currentTarget.style.transform = 'translateX(3px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(255, 107, 107, 0.06)';
                          e.currentTarget.style.borderColor = 'rgba(255, 107, 107, 0.2)';
                          e.currentTarget.style.transform = 'none';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                          <span
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: '#ff6b6b',
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ fontSize: '0.84rem', lineHeight: 1.4, color: '#f9fafb' }}>
                            {item.message}
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            fontSize: '0.75rem',
                            color: '#d9fb06',
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          <span>Fix</span>
                          <ArrowRight size={13} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(0, 0, 0, 0.3)',
          }}
        >
          <span style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.5)' }}>
            All entered information has been safely preserved.
          </span>
          <button
            type="button"
            onClick={onClose}
            className="dist-btn dist-btn-secondary"
            style={{
              padding: '0.55rem 1.25rem',
              fontSize: '0.86rem',
              borderRadius: '8px',
            }}
          >
            Review Fields
          </button>
        </div>
      </div>
    </div>
  );
}
